'use client';

import type { UserProgress } from '@repo/lib';
import { useCallback, useEffect, useState } from 'react';
import {
  getOfferGateState,
  markOfferPending,
  OFFER_STORAGE_KEY,
  type OfferGateReason,
  type OfferGateState,
  resolveOfferGate,
} from '@/lib/offer-gate';

type OfferGateResolution = 'accepted' | 'skipped';

export function useOfferGate(userProgress?: UserProgress | null) {
  const [state, setState] = useState<OfferGateState | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const isHardVariant = userProgress?.paywallVariant === 'hard';
  const isFreePlan = userProgress?.subscriptionStatus === 'stargazer';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hydrate = () => {
      setState(getOfferGateState());
      setIsHydrated(true);
    };

    hydrate();

    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== OFFER_STORAGE_KEY) {
        return;
      }
      hydrate();
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!(isHydrated && isHardVariant && isFreePlan)) {
      return;
    }
    if (state) {
      return;
    }
    markOfferPending('upsell');
    setState(getOfferGateState());
  }, [isHydrated, isHardVariant, isFreePlan, state]);

  const shouldShowOffer = Boolean(isHydrated && userProgress && isFreePlan && state?.pending);

  const queueOffer = useCallback((reason: OfferGateReason = 'signup') => {
    markOfferPending(reason);
    setState(getOfferGateState());
  }, []);

  const resolveOffer = useCallback((action: OfferGateResolution) => {
    resolveOfferGate(action);
    setState(getOfferGateState());
  }, []);

  return {
    state,
    shouldShowOffer,
    markPending: queueOffer,
    resolveOffer,
  };
}
